import { el } from '../../dom';
import { richText } from '../../ui/icons';
import { itemIconSrc } from '../../ui/item-icons';
import type { Crop } from '../../game-api';
import { fmtGrowth, growthBarPct, slowestGrowth, sortSeeds } from './planting';

/**
 * The seed picker's list body.
 *
 * Two things were wrong with the old inline version in plots.ts:
 *
 * 1. Icons were pulled straight from `/sprites/crops/produce_<id>.png`, which is
 *    bbox-trimmed art at whatever size and aspect the pack sliced it — a tall
 *    grape column and a squat turnip in the same `w-8 h-8 object-contain` box
 *    end up different apparent sizes and sit high, low or off to one side. The
 *    baked `/sprites/icons/` set (see ui/item-icons.ts) is the SAME art re-centred
 *    on a common 32×32 canvas, longest side filling, so every seed sits
 *    identically. The slot is a fixed square with the image centred in it.
 * 2. Cost/yield/time/level were one run-on mono line with no way to compare the
 *    only figure that matters. Time now also gets a bar, scaled against the
 *    slowest crop on screen (see growthBarPct for why a bar and not just order).
 *
 * SEEDS OWNED (owner bug, 2026-08-17): "I used the seed press… now I have 16
 * wheat seeds, 28 eggplant and 6 corn — but I see only wheat seeds in my plant-a-
 * seed popup. Also in this popup I can't see the amount of seeds I have."
 *
 * Two separate breaks, both here:
 *  1. The tutorial slice below cut the list to ONE crop, and it is keyed on a
 *     localStorage flag (`tb-tutorial-stage`) that stays un-'done' for anyone who
 *     never walked the tour to its end — so a player who had been pressing seeds
 *     for hours was still being shown wheat and nothing else. It now never hides a
 *     crop the player is actually holding seed for: he pressed it, he can see it.
 *  2. Nothing on the row said how many seeds you held, or that holding one makes
 *     the planting FREE (field.service.plant spends a bag seed before it ever
 *     debits coins). The count is now the first thing on the cost line, and the
 *     coin price is only shown when there is no seed to spend — because when
 *     there IS one, the coin price is not what you are about to pay.
 *
 * `seedsOwned` is cropId → count, from GET /field/seeds (the server's numbers —
 * this module never counts a bag itself).
 *
 * NOT shown because the crop payload does not carry it: sell value / gold per hour.
 */
export function buildSeedList(
  crops: Crop[],
  level: number,
  tutorialActive: boolean,
  onPick: (crop: Crop) => void,
  seedsOwned: Record<string, number> = {},
): HTMLElement {
  const list = el('div', 'max-h-[60vh] overflow-y-auto pr-1');
  const heldOf = (id: string) => seedsOwned[id] ?? 0;

  // Until the tutorial is finished, offer only the first starter seed — PLUS every
  // crop the player already holds seed for (see the header: hiding those was the
  // bug).
  const sorted = sortSeeds(crops, level);
  const shown = tutorialActive
    ? sorted.filter((c, i) => i === 0 || heldOf(c.id) > 0)
    : sorted;
  const slowest = slowestGrowth(shown);

  for (const crop of shown) {
    const locked = crop.unlockLevel > level;
    const cls = 'w-full flex items-center gap-3 p-2 mb-1.5 rounded border text-left min-h-[56px] '
      + (locked ? 'border-border opacity-40 grayscale cursor-not-allowed' : 'border-border hover:border-gold');
    const props = locked ? { disabled: true } : { onClick: () => onPick(crop) };

    // Fixed 40px square slot, image centred inside it: the row height no longer
    // depends on which crop it is, so the whole column lines up.
    const slot = el('div', 'w-10 h-10 shrink-0 flex items-center justify-center', {},
      el('img', 'max-w-full max-h-full object-contain', {
        src: itemIconSrc(`produce_${crop.id}`), alt: crop.name, style: 'image-rendering:pixelated',
      }));

    const held = heldOf(crop.id);
    const kindMark = crop.kind === 'tree' ? ' 🌳' : crop.kind === 'bush' ? ' 🌿' : '';
    const bar = el('div', 'h-1.5 mt-1 rounded bg-black/40 overflow-hidden', {},
      el('div', 'h-full bg-gold/70', { style: `width:${growthBarPct(crop.growthMs, slowest)}%` }));

    // The bag count, on the icon itself — the one number the owner asked for, in
    // the place the eye already goes.
    const slotWrap = el('div', 'relative shrink-0', {}, slot);
    if (held > 0) {
      slotWrap.append(el('span',
        'absolute -bottom-1 -right-1 px-1 rounded bg-black/80 text-gold text-[10px] font-mono leading-tight',
        {}, `×${held}`));
    }

    list.append(el('button', cls, props,
      slotWrap,
      el('div', 'flex-1 min-w-0', {},
        el('div', 'flex items-baseline justify-between gap-2', {},
          el('span', 'text-sm inline-flex items-center gap-1', {}, ...richText(`${crop.name}${kindMark}`)),
          el('span', 'text-[11px] font-mono text-text-dim shrink-0', {}, fmtGrowth(crop.growthMs))),
        bar,
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1 mt-1', {},
          // Held seed is spent BEFORE coins (field.service.plant), so quoting the
          // coin price to a player holding seed would name a cost he is not paying.
          ...richText(`${held > 0 ? `🌱 ${held} seed${held === 1 ? '' : 's'} — free` : `🪙 ${crop.seedCost}`}`
            + ` · ×${crop.yield}${crop.kind !== 'crop' ? ' regrows' : ''} · ${crop.xp} XP · Lv${crop.unlockLevel}`)),
        locked ? el('div', 'text-[11px] text-error/80', {}, `Locked — reach level ${crop.unlockLevel}`) : null)));
  }
  return list;
}

/**
 * The one rule nobody discovers on their own, stated where the seed is chosen:
 * a dry bed does not tick at all. Repeated on the plot itself (plots.ts) because
 * this modal is closed by the time the player is staring at a stalled bed.
 */
export function waterNotice(): HTMLElement {
  return el('div', 'text-[11px] text-sky-300/90 mb-2 leading-snug', {},
    'A seed does not grow until the bed is watered — dry beds are frozen, not slow.');
}
